// emailContentGenerator.ts
import getPriorities from "../store/score/getPriorities";
import { useTranslation } from "../i18n/hooks";

type ScoreRange = 'low' | 'average' | 'high';

const CONTENT = {
    subjectLines: {
      low: [
        "{{Store_Name}}: Missing product specs are costing you 67% visibility",
        "{{Store_Name}}: Add high-performing keywords to boost visibility by 89%",
        "{{Store_Name}}: Your listings lack photos that drive 2.3x sales"
      ],
      average: [
        "{{Store_Name}}: Optimize your titles to get 45% more clicks",
        "{{Store_Name}}: Add engaging product photos to boost conversions by 127%",
        "{{Store_Name}}: Target trending keywords for 56% more traffic"
      ],
      high: [
        "{{Store_Name}}: Fine-tune your listings to increase revenue by 23%",
        "{{Store_Name}}: Optimize categories to boost qualified traffic 34%",
        "{{Store_Name}}: Capture untapped markets with advanced optimization"
      ]
    },
  
    quickWinIntros: {
      low: "Here are your priority actions to boost performance:",
      average: "Here are your next steps to reach the top:",
      high: "Here's what's working well in your store:"
    },
  
    overallMessages: {
      low: "Top sellers in your category average 77% - let's close this gap",
      average: "You're ahead of 65% of sellers in your category",
      high: "You're among the top 10% of sellers in your category"
    },
  
    metricMessages: {
      conversion: {
        low: "Your listings need attention - quick fixes could boost sales by 45%",
        average: "Your listings are performing well with room to grow",
        high: "Your listings are converting at top-tier rates"
      },
      discovery: {
        low: "Shoppers struggle to filter and find your products - missing category mappings and tags",
        average: "Good product organization with some gaps in filtering options",
        high: "Perfect product organization - shoppers can find exactly what they need"
      },
      search: {
        low: "Missing critical SEO elements - keywords and alt texts need optimization",
        average: "Good SEO foundation - opportunity to target more valuable keywords",
        high: "Excellent SEO optimization across all listing elements"
      }
    }
  };
  
  function getScoreRange(score: number): ScoreRange {
    if (score <= 40) return 'low';
    if (score <= 70) return 'average';
    return 'high';
  }
  
  export function generateEmailContent(
    storeName: string,
    scores: {
      conversion: number;
      discovery: number;
      search: number;
      average: number;
      titleQuality: number;
      descriptionQuality: number;
      imageQuality: number;
      seoQuality: number;
      altTagsQuality: number;
      categoryQuality: number;
    }
  ) {
    const { t } = useTranslation();
    
    const averageRange = getScoreRange(scores.average);
    
    // Get priorities using getPriorities function
    const priorities = getPriorities({
      titleQuality: scores.titleQuality,
      descriptionQuality: scores.descriptionQuality,
      imageQuality: scores.imageQuality,
      seoQuality: scores.seoQuality,
      altTagsQuality: scores.altTagsQuality,
      categoryQuality: scores.categoryQuality,
      t
    });
  
    // Get priority messages
    const quickWins = priorities.map(priority => priority.message);
  
    // Randomly select a subject line
    const subjectLines = CONTENT.subjectLines[averageRange];
    const selectedSubject = subjectLines[Math.floor(Math.random() * subjectLines.length)];
  
    return {
      subjectLine: selectedSubject.replace('{{Store_Name}}', storeName),
      quickWinsIntro: CONTENT.quickWinIntros[averageRange],
      quickWins,
      overallMessage: CONTENT.overallMessages[averageRange],
      metrics: {
        conversion: CONTENT.metricMessages.conversion[getScoreRange(scores.conversion)],
        discovery: CONTENT.metricMessages.discovery[getScoreRange(scores.discovery)],
        search: CONTENT.metricMessages.search[getScoreRange(scores.search)]
      }
    };
  }